import { DisclosureButton, PageHeader } from '@lawpilots/design-system'
import { Box, Stack } from '@mui/material'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { LoadingPanel, PageLayout, SectionHeading } from '@/common/components'
import { getUserDisplayName } from '@/features/auth'
import { FEATURES } from '@/features.config'
import { usePageVisited } from '@/hooks'
import { LearnerCourseDto } from '@/services/api/tenant/learnerCourses'
import { useListMyCoursesQuery } from '@/services/api/tenant/learnerCoursesEnhanced'
import { RootState, useTypedSelector } from '@/store'
import { useCertificateDownload } from '../../certificates/hooks/useCertificateDownload'
import { LearnerEmptyState } from '../../components/LearnerEmptyState'
import { MyCourseCard } from '../components/MyCourseCard'
import { groupCourseSections } from '../utils/courseSections'

const SectionStyles = { display: 'flex', flexDirection: 'column', gap: 2 }

// Figma: a column per card that fits at 400px, each stretching to 480px and no further, so a wide
// screen leaves space at the end rather than a row of over-wide cards.
//
// The maximum has to stay flexible. `auto-fill` counts columns off the maximum whenever that is a
// definite length, so `minmax(400px, 480px)` fits one column into 931px of space - room for two.
// `1fr` sends it back to counting off the 400px minimum, and the cards carry the 480px cap.
//
// Below md a phone gets a single full-width column: a 400px minimum overflows a 320px screen.
// Capped at two full-width cards and the gap between them, so the tracks land on 480px rather
// than stretching wider than the cards and opening a hole in the middle of the row.
const CourseGridMaxWidth = `${2 * 480 + 16}px`

const CourseGridStyles = {
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', md: 'repeat(auto-fill, minmax(400px, 1fr))' },
  gap: 2,
  maxWidth: CourseGridMaxWidth,
  '& > *': { maxWidth: { md: '480px' } },
}

const MyCourses = (): JSX.Element => {
  const { t } = useTranslation('general')
  const accessedTenant = useTypedSelector((state: RootState) => state.authSlice.tenant)
  const currentUser = useTypedSelector((state: RootState) => state.authSlice.user)
  const { data: courses = [], isLoading } = useListMyCoursesQuery({ tenantId: accessedTenant.id })
  const [showInactive, setShowInactive] = useState(false)
  const downloadCertificate = useCertificateDownload(getUserDisplayName(currentUser))
  usePageVisited('My Courses')

  const sections = useMemo(() => groupCourseSections(courses), [courses])

  // The phone card offers the certificate in place of a replay; the course carries its own link.
  const onDownloadCertificate = (course: LearnerCourseDto) =>
    downloadCertificate({ url: course.certificate_url, obtained_on: course.last_completed_at ?? '' })

  const section = (title: string, sectionCourses: LearnerCourseDto[]) =>
    sectionCourses.length > 0 && (
      <Box component="section" sx={SectionStyles} aria-label={title}>
        <SectionHeading title={title} />
        <Box sx={CourseGridStyles}>
          {sectionCourses.map((course) => (
            <MyCourseCard key={course.enrollment_id} course={course} onDownloadCertificate={onDownloadCertificate} />
          ))}
        </Box>
      </Box>
    )

  return (
    <PageLayout feature={FEATURES.MY_COURSES}>
      <Stack spacing={1}>
        <PageHeader title={t('my_courses_title')} />
        {/* Empty branch lives inside the loading wrapper so an empty array mid-fetch cannot flash
            the empty state (FS-272). */}
        <LoadingPanel loading={isLoading}>
          {courses.length === 0 ? (
            <LearnerEmptyState title={t('my_courses_empty_title')} description={t('my_courses_empty_description')} />
          ) : (
            <Stack spacing={4}>
              {section(t('my_courses_section_enrolled'), sections.enrolled)}
              {section(t('my_courses_section_completed'), sections.completed)}
              {sections.inactive.length > 0 && (
                <Stack spacing={4}>
                  <Box>
                    <DisclosureButton open={showInactive} onClick={() => setShowInactive((open) => !open)}>
                      {showInactive ? t('my_courses_hide_inactive') : t('my_courses_show_inactive')}
                    </DisclosureButton>
                  </Box>
                  {showInactive && section(t('my_courses_section_inactive'), sections.inactive)}
                </Stack>
              )}
            </Stack>
          )}
        </LoadingPanel>
      </Stack>
    </PageLayout>
  )
}

export default MyCourses
